<script setup lang="ts">
// Signal-health tile: one per fund. Status chip, four measures (last 250 sessions with the
// prior 750 in grey), a rolling-beta sparkline with the trigger level, and the reason
// footer. Click / Enter / Space expands the confidence-tier and size-bucket tables.
import { computed, ref } from 'vue'
import type { SignalHealthFund } from '../api/client'
import {
  statusColour, statusWord, fmtBeta, fmtBps, fmtHit, fmtP, fmtAckDate,
  isAcknowledged, triggerLevel, sparkline,
} from './signalHealthHelpers'

const props = defineProps<{
  fund: SignalHealthFund
  /** yyyy-MM-dd, London */
  today: string
}>()

const expanded = ref(false)
function toggle() { expanded.value = !expanded.value }

const chipStyle = computed(() => ({
  background: statusColour(props.fund.status),
  color: props.fund.status === 'amber' ? '#1a1a1a' : '#fff',
}))

const rows = computed(() => {
  const cur = props.fund.last250
  const pri = props.fund.prior750
  const row = (
    key: string, label: string, fmt: (v: number | null | undefined) => string,
    pick: (w: NonNullable<typeof cur>) => number | null,
  ) => ({
    key,
    label,
    value: cur ? fmt(pick(cur)) : '—',
    prior: pri ? `prior ${fmt(pick(pri))}` : '',
  })
  return [
    row('beta', `fund β to ${props.fund.ref}`, fmtBeta, w => w.fundBetaRef),
    row('bps', 'signed bp/day', fmtBps, w => w.signedBps),
    row('hit', 'hit rate', fmtHit, w => w.hit),
    row('p', 'P(β>0)', fmtP, w => w.pBetaY1Pos),
  ]
})

const spark = computed(() =>
  sparkline(props.fund.rolling.map(r => r.fundBetaRef), triggerLevel(props.fund), 160, 36))

const acknowledged = computed(() => isAcknowledged(props.fund, props.today))
</script>

<template>
  <div
    class="sh-tile"
    :data-testid="`sh-tile-${fund.fundId}`"
    :data-fund="fund.fundId"
    tabindex="0"
    role="button"
    :aria-expanded="expanded"
    @click="toggle"
    @keydown.enter="toggle"
    @keydown.space.prevent="toggle"
  >
    <div class="sh-head">
      <div class="sh-title">{{ fund.label }}<span class="sh-ref"> · {{ fund.ref }}</span></div>
      <span class="sh-chip" data-testid="sh-chip" :style="chipStyle">{{ statusWord(fund.status) }}</span>
    </div>

    <div class="sh-metrics">
      <div v-for="r in rows" :key="r.key" class="sh-row" :data-testid="`sh-row-${r.key}`">
        <span class="sh-label">{{ r.label }}</span>
        <span class="sh-nums">
          <span class="sh-value">{{ r.value }}</span>
          <span class="sh-prior">{{ r.prior }}</span>
        </span>
      </div>
    </div>

    <svg
      v-if="fund.rolling.length >= 2"
      class="sh-spark"
      data-testid="sh-spark"
      viewBox="0 0 160 36"
      preserveAspectRatio="none"
    >
      <polyline :points="spark.points" fill="none" stroke="#9ecbff" stroke-width="1.5" vector-effect="non-scaling-stroke" />
      <line
        v-if="spark.levelY !== null"
        class="sh-trigger"
        data-testid="sh-trigger-line"
        x1="0"
        x2="160"
        :y1="spark.levelY"
        :y2="spark.levelY"
        stroke="#f44336"
        stroke-width="1"
        stroke-dasharray="3 3"
        vector-effect="non-scaling-stroke"
      />
    </svg>

    <div class="sh-footer" :class="{ 'is-stale': !!fund.staleSince }" data-testid="sh-footer">
      {{ fund.reason }}<template v-if="acknowledged">
        · <span class="sh-ack" :title="fund.acknowledgedNote ?? ''">acknowledged until {{ fmtAckDate(fund.acknowledgedUntil!) }}</span>
      </template>
    </div>

    <div v-if="expanded" class="sh-detail" data-testid="sh-detail" @click.stop @keydown.stop>
      <div class="sh-caption">Confidence tiers (|z|, full sample)</div>
      <table class="sh-table">
        <thead><tr><th>|z|</th><th>n</th><th>hit</th><th>bp</th></tr></thead>
        <tbody>
          <tr v-for="t in fund.tiers" :key="t.absZ">
            <td>{{ t.absZ }}</td><td>{{ t.n }}</td><td>{{ fmtHit(t.hit) }}</td><td>{{ fmtBps(t.signedBps) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="sh-caption">Size buckets (|x| in sd units)</div>
      <table class="sh-table">
        <thead><tr><th>window</th><th>sd</th><th>bp</th><th>n</th><th>β y0</th><th>β y1</th><th>hit</th><th>bp/day</th></tr></thead>
        <tbody>
          <tr v-for="b in fund.buckets" :key="`${b.window}|${b.bucketSd}|${b.bp}`">
            <td>{{ b.window }}</td><td>{{ b.bucketSd }}</td><td>{{ b.bp }}</td><td>{{ b.n }}</td>
            <td>{{ fmtBeta(b.betaY0) }}</td><td>{{ fmtBeta(b.betaY1) }}</td>
            <td>{{ fmtHit(b.hit) }}</td><td>{{ fmtBps(b.signedBps) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="sh-meta">offset {{ fund.offsetRows ?? '—' }} · last row {{ fund.lastDate ?? '—' }} · n {{ fund.last250?.n ?? '—' }}</div>
    </div>
  </div>
</template>

<style scoped>
.sh-tile {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 0.75rem;
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  cursor: pointer;
}
.sh-tile:focus-visible { outline: 1px solid #9ecbff; }

.sh-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}
.sh-title {
  font-size: 0.8rem;
  color: #aaa;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.sh-ref { color: #777; font-weight: 400; }
.sh-chip {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.1rem 0.45rem;
  border-radius: 3px;
}

.sh-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}
.sh-row { display: flex; justify-content: space-between; gap: 0.5rem; }
.sh-label { color: #888; }
.sh-nums { display: flex; gap: 0.5rem; }
.sh-value { color: #ccc; }
.sh-prior { color: #777; }

.sh-spark { width: 100%; height: 36px; display: block; }

.sh-footer { color: #aaa; font-size: 0.75rem; }
.sh-footer.is-stale { color: #fbc02d; font-style: italic; }
.sh-ack { color: #888; }

.sh-detail {
  border-top: 1px solid #333;
  padding-top: 0.4rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  color: #ccc;
}
.sh-caption { color: #888; margin: 0.3rem 0 0.15rem; }
.sh-table { width: 100%; border-collapse: collapse; }
.sh-table th { color: #777; font-weight: 400; text-align: left; }
.sh-table th, .sh-table td { padding: 0.05rem 0.3rem 0.05rem 0; }
.sh-meta { color: #777; margin-top: 0.4rem; }
</style>
